/**
 * Recovery for lazy-chunk load failures caused by a deploy.
 *
 * Build assets are content hashed, so a new deploy publishes new file names and
 * removes the old ones. Any tab that was already open — or that loaded a stale
 * `index.html` — still holds references to the previous names, so its next
 * `import()` fails. The fix is to reload onto the current `index.html`, which is
 * the only file that knows the current hashes.
 */

const LAST_ATTEMPT_KEY = "chunkReloadAt"
const ATTEMPT_COUNT_KEY = "chunkReloadCount"

/** Don't loop: leave time for the reload to actually complete before retrying. */
const MIN_GAP_BETWEEN_ATTEMPTS = 10 * 1000
/** After this long with no failure we treat the situation as recovered. */
const ATTEMPT_WINDOW = 5 * 60 * 1000
const MAX_ATTEMPTS = 3

/**
 * Messages browsers use when a dynamic `import()` cannot be fetched or parsed.
 * These differ per engine, which is why matching on Chrome's wording alone
 * silently skipped recovery in Safari.
 */
const CHUNK_ERROR_PATTERNS = [
  // Chrome / Edge
  "failed to fetch dynamically imported module",
  // Firefox
  "error loading dynamically imported module",
  // Safari
  "importing a module script failed",
  "module script failed to load",
  // Safari's generic network failure, which is what a dropped chunk looks like
  "load failed",
  // Server answered with HTML (an SPA fallback or an error page) for a .js request
  "is not a valid javascript mime type",
  "expected a javascript module script",
  // Older webpack-style chunk loading, still thrown by some dependencies
  "chunkloaderror",
  "loading chunk",
  "loading css chunk",
]

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false

  const name = typeof (error as any).name === "string" ? (error as any).name : ""
  if (name === "ChunkLoadError") return true

  const message =
    typeof (error as any).message === "string" ? (error as any).message : String(error)
  const haystack = message.toLowerCase()

  return CHUNK_ERROR_PATTERNS.some(pattern => haystack.includes(pattern))
}

function readInt(key: string): number {
  const raw = window.localStorage.getItem(key)
  const parsed = raw === null ? NaN : parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * True when we have already burned through our reload attempts, i.e. reloading
 * is not fixing the problem and we should show the user an error instead of
 * bouncing them round a redirect loop.
 */
export function hasExhaustedChunkReloads(): boolean {
  try {
    const lastAttempt = readInt(LAST_ATTEMPT_KEY)
    if (!lastAttempt || Date.now() - lastAttempt > ATTEMPT_WINDOW) return false
    return readInt(ATTEMPT_COUNT_KEY) >= MAX_ATTEMPTS
  } catch {
    return false
  }
}

/** Called once the app has rendered successfully, so the next deploy starts fresh. */
export function clearChunkReloadState() {
  try {
    window.localStorage.removeItem(LAST_ATTEMPT_KEY)
    window.localStorage.removeItem(ATTEMPT_COUNT_KEY)
  } catch {
    // localStorage can throw in private mode — recovery must not depend on it
  }
}

/**
 * Reload onto the current build. Returns false when we've already tried too
 * often, so the caller can render an error instead.
 *
 * A plain `location.reload()` is not enough on its own: it can be served the
 * same cached `index.html` that caused the failure, landing straight back on the
 * broken bundle. We re-request the document with `cache: "reload"` first, which
 * forces a network fetch and refreshes the HTTP cache entry, then reload.
 */
export function reloadForNewBuild(): boolean {
  let lastAttempt = 0
  let attempts = 0

  try {
    lastAttempt = readInt(LAST_ATTEMPT_KEY)
    attempts = readInt(ATTEMPT_COUNT_KEY)

    // A failure long after the last one is a new incident, not a retry loop
    if (lastAttempt && Date.now() - lastAttempt > ATTEMPT_WINDOW) {
      attempts = 0
    }

    if (lastAttempt && Date.now() - lastAttempt < MIN_GAP_BETWEEN_ATTEMPTS) return false
    if (attempts >= MAX_ATTEMPTS) return false

    window.localStorage.setItem(LAST_ATTEMPT_KEY, Date.now().toString())
    window.localStorage.setItem(ATTEMPT_COUNT_KEY, (attempts + 1).toString())
  } catch {
    // If we can't track attempts we still reload, but only once per page load
    if (reloadRequested) return false
  }

  if (reloadRequested) return false
  reloadRequested = true

  const done = () => window.location.reload()

  try {
    window
      .fetch(window.location.href, { cache: "reload", credentials: "same-origin" })
      .then(done, done)
  } catch {
    done()
  }

  return true
}

let reloadRequested = false
